import Markdown from 'shared/components/Markdown';
import { SPACINGS } from 'shared/styles/variables';
import styled from 'styled-components';

import Spacer from 'design-system/components/Spacer';
import Button, { ButtonType } from 'inquiry/components/Button';
import ConfirmFileUploadView from 'inquiry/components/ConfirmFileUploadView';
import ImageOriented from 'inquiry/components/ImageOriented';
import InlineLoadingIndicator from 'inquiry/components/InlineLoadingIndicator';
import ThemedParagraph from 'inquiry/components/ThemedParagraph';
import ThemedTitle from 'inquiry/components/ThemedTitle';
import { useStepGovernmentIdStyleContext } from 'inquiry/context/StepGovernmentIdStyleContext';
import { StepGovernmentIdConfig } from 'lib/interfaces/StepsConfig';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer/PositionableButtonContainer';
import { StoredFileUpload } from 'verify/lib/interfaces/fileUploads';
import { MIN_HEIGHTS } from 'verify/styles/variables';

const Container = styled.div`
  display: flex;
  flex-direction: column;
`;

const Header = styled('div')`
  min-height: ${MIN_HEIGHTS.header};
  margin-bottom: ${SPACINGS.base * 4}px;
`;

const ImagePositioner = styled('div')`
  img {
    display: block;
    margin-left: auto;
    margin-right: auto;
    max-width: 100%;
    max-height: 320px;

    margin-bottom: ${SPACINGS.base * 2}px;
  }
`;

const Minify = styled('div')`
  display: inline;

  img {
    height: 12px;
    margin-bottom: 4px;
  }
`;

interface InquiryGovernmentIdCheckViewProps {
  assets: StepGovernmentIdConfig['assets']['check-page'];
  currentUpload: StoredFileUpload;
  isProcessing: boolean;
  localizations: StepGovernmentIdConfig['localizations']['check-page'];
  handleContinue: () => void;
  handleRetry: () => void;
}

export default function InquiryGovernmentIdCheckView(props: InquiryGovernmentIdCheckViewProps) {
  const { assets, currentUpload, localizations, handleContinue, handleRetry, isProcessing } = props;

  const { base, buttons: buttonStyles } = useStepGovernmentIdStyleContext();

  if (!['image/jpeg', 'image/png'].includes(currentUpload.fileType)) {
    return (
      <ConfirmFileUploadView
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        customFileIcon={assets['icon-file']}
        fileName={currentUpload.filename}
        isProcessing={isProcessing}
        handleContinue={handleContinue}
        handleRetry={handleRetry}
        title={localizations['confirm-title']}
        prompt={localizations['confirm-prompt']}
        uploading={localizations['confirm-uploading']}
        btnSubmit={localizations['confirm-btn-submit']}
        btnRetake={localizations['confirm-btn-retake']}
        titleStyles={base?.titleStyle}
        textStyles={base?.textStyle}
        buttonStyles={buttonStyles}
      />
    );
  }

  return (
    <Container>
      <Header>
        <ThemedTitle styles={base?.titleStyle}>
          <Markdown>{localizations.title}</Markdown>
        </ThemedTitle>
        <ThemedParagraph styles={base?.textStyle}>
          <Markdown>{localizations.prompt}</Markdown>
        </ThemedParagraph>
      </Header>

      <ImagePositioner>
        <ImageOriented src={currentUpload.dataUrl} alt='upload preview' />
      </ImagePositioner>

      <PositionableButtonContainer>
        <Button
          id='government_id__use-image'
          buttonType={ButtonType.Primary}
          styles={buttonStyles?.[ButtonType.Primary]}
          disabled={isProcessing}
          onClick={handleContinue}
          data-test='government-id-check__continue'
        >
          {isProcessing ? (
            <span>
              {localizations.uploading}
              <Minify>
                <InlineLoadingIndicator />
              </Minify>
            </span>
          ) : (
            localizations['btn-submit']
          )}
        </Button>

        {/* Assume spacing is specified by styles if present */}
        {buttonStyles == null && <Spacer height={2} />}

        <Button
          id='government_id__retake'
          buttonType={ButtonType.Secondary}
          styles={buttonStyles?.[ButtonType.Secondary]}
          disabled={isProcessing}
          onClick={handleRetry}
          data-test='government-id-check__retake'
        >
          {localizations['btn-retake']}
        </Button>
      </PositionableButtonContainer>
    </Container>
  );
}
